/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CostCenter } from '../models/CostCenter';
import type { CostCenterRequest } from '../models/CostCenterRequest';
import type { PaginatedCostCenterList } from '../models/PaginatedCostCenterList';
import type { PatchedCostCenterRequest } from '../models/PatchedCostCenterRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class CostCentersService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * API endpoint to view and edit company cost centers
     * @returns PaginatedCostCenterList
     * @throws ApiError
     */
    public companiesCostCentersList({
        parentCompany,
        limit,
        offset,
    }: {
        parentCompany: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedCostCenterList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/cost-centers/',
            path: {
                'parent_company': parentCompany,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * API endpoint to view and edit company cost centers
     * @returns CostCenter
     * @throws ApiError
     */
    public companiesCostCentersCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody: CostCenterRequest,
    }): CancelablePromise<CostCenter> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/cost-centers/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company cost centers
     * @returns CostCenter
     * @throws ApiError
     */
    public companiesCostCentersRetrieve({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this cost center.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<CostCenter> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/cost-centers/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * API endpoint to view and edit company cost centers
     * @returns CostCenter
     * @throws ApiError
     */
    public companiesCostCentersUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A UUID string identifying this cost center.
         */
        id: string,
        parentCompany: string,
        requestBody: CostCenterRequest,
    }): CancelablePromise<CostCenter> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{parent_company}/cost-centers/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company cost centers
     * @returns CostCenter
     * @throws ApiError
     */
    public companiesCostCentersPartialUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A UUID string identifying this cost center.
         */
        id: string,
        parentCompany: string,
        requestBody?: PatchedCostCenterRequest,
    }): CancelablePromise<CostCenter> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{parent_company}/cost-centers/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company cost centers
     * @returns void
     * @throws ApiError
     */
    public companiesCostCentersDestroy({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this cost center.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{parent_company}/cost-centers/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

}
